import React, { memo, useMemo } from 'react';
import { isFit } from '../../utils/hydration';
import { isNum } from '../../utils/primitives';
import { getImageColorStyles } from '../../utils/styles';
import {
  DEFAULT_CHEVRON_GAP,
  DROPDOWN_CHEVRON_CLEARANCE,
  DROPDOWN_FIELDS,
  INNER_PADDING_KEYS,
  INNER_PADDING_LEFT,
  INNER_PADDING_RIGHT,
  INPUT_BOX_FIELDS,
  MULTISELECT_FIELD,
  nativeIconGapStyles,
  RESET_INLINE_PADDING_CSS
} from '../styles';
import {
  LABEL_TEXT_ALIGN_DEFAULT,
  getLabelGapDefault
} from '../utils/labelStyleResolver';
import EditableFieldLabel from '../components/EditableFieldLabel';

type VisiblePositions = Record<string, boolean[]>;

const fieldLoaders = {
  AddressLine1: () =>
    import(/* webpackChunkName: "AddressField" */ './AddressLine1Field'),
  AudioRecordingField: () =>
    import(
      /* webpackChunkName: "AudioRecordingField" */ './AudioRecordingField'
    ),
  ButtonGroupField: () =>
    import(/* webpackChunkName: "ButtonGroupField" */ './ButtonGroupField'),
  CheckboxField: () =>
    import(/* webpackChunkName: "CheckboxField" */ './CheckboxField'),
  CheckboxGroupField: () =>
    import(/* webpackChunkName: "CheckboxGroupField" */ './CheckboxGroupField'),
  ColorPickerField: () =>
    import(/* webpackChunkName: "ColorPickerField" */ './ColorPickerField'),
  CustomField: () =>
    import(/* webpackChunkName: "CustomField" */ './CustomField'),
  DateSelectorField: () =>
    import(/* webpackChunkName: "DateSelectorField" */ './DateSelectorField'),
  DropdownField: () =>
    import(/* webpackChunkName: "DropdownField" */ './DropdownField'),
  DropdownMultiField: () =>
    import(/* webpackChunkName: "DropdownMultiField" */ './DropdownMultiField'),
  FileUploadField: () =>
    import(/* webpackChunkName: "FileUploadField" */ './FileUploadField'),
  MatrixField: () =>
    import(/* webpackChunkName: "MatrixField" */ './MatrixField'),
  PasswordField: () =>
    import(/* webpackChunkName: "PasswordField" */ './PasswordField'),
  PaymentMethodField: () =>
    import(/* webpackChunkName: "PaymentMethodField" */ './PaymentMethodField'),
  PhoneField: () => import(/* webpackChunkName: "PhoneField" */ './PhoneField'),
  PinInputField: () =>
    import(/* webpackChunkName: "PinInputField" */ './PinInputField'),
  QRScanner: () => import(/* webpackChunkName: "QRScanner" */ './QRScanner'),
  RadioButtonGroupField: () =>
    import(
      /* webpackChunkName: "RadioButtonGroupField" */ './RadioButtonGroupField'
    ),
  RatingField: () =>
    import(/* webpackChunkName: "RatingField" */ './RatingField'),
  SignatureField: () =>
    import(/* webpackChunkName: "SignatureField" */ './SignatureField'),
  SliderField: () =>
    import(/* webpackChunkName: "SliderField" */ './SliderField'),
  TextField: () => import(/* webpackChunkName: "TextField" */ './TextField'),
  TextArea: () => import(/* webpackChunkName: "TextArea" */ './TextArea')
};

type FieldComponentKey = keyof typeof fieldLoaders;

const getPositionKey = (element: any) => element.position?.join(',') || 'root';

type PreloadableField = React.ComponentType<any> & {
  preload: () => Promise<any>;
};

// Share the resolved component between preload and render. A separate
// React.lazy wrapper can still suspend on its first render even after an
// external import() preload has resolved.
export const createPreloadableField = (
  name: string,
  load: () => Promise<any>
): PreloadableField => {
  let Component: React.ComponentType<any> | null = null;
  let preloadPromise: Promise<any> | null = null;
  let loadError: any = null;

  const preload = () => {
    if (!preloadPromise) {
      loadError = null;
      preloadPromise = load()
        .then((module) => {
          // A host app that cancels Vite's vite:preloadError event turns a
          // failed chunk request into a promise resolving to undefined, so a
          // resolved import is not proof the chunk actually loaded.
          if (!module?.default) {
            throw new Error(
              `Field chunk "${name}" resolved without a default export`
            );
          }
          Component = module.default;
          return module;
        })
        .catch((error) => {
          preloadPromise = null;
          loadError = error;
          throw error;
        });
    }
    return preloadPromise;
  };

  const Field = (props: any) => {
    if (Component) return React.createElement(Component, props);
    if (loadError) throw loadError;
    throw preload();
  };
  Field.preload = preload;

  return Field;
};

const preloadableFields = Object.fromEntries(
  Object.entries(fieldLoaders).map(([name, load]) => [
    name,
    createPreloadableField(name, load)
  ])
) as Record<FieldComponentKey, PreloadableField>;

const getFieldComponentKey = (servarType?: string): FieldComponentKey => {
  switch (servarType) {
    case 'matrix':
      return 'MatrixField';
    case 'date_selector':
      return 'DateSelectorField';
    case 'signature':
      return 'SignatureField';
    case 'qr_scanner':
      return 'QRScanner';
    case 'custom':
      return 'CustomField';
    case 'file_upload':
      return 'FileUploadField';
    case 'audio_recording':
      return 'AudioRecordingField';
    case 'button_group':
      return 'ButtonGroupField';
    case 'checkbox':
      return 'CheckboxField';
    case 'dropdown':
    case 'gmap_state':
    case 'gmap_country':
      return 'DropdownField';
    case 'dropdown_multi':
      return 'DropdownMultiField';
    case 'pin_input':
      return 'PinInputField';
    case 'multiselect':
      return 'CheckboxGroupField';
    case 'select':
      return 'RadioButtonGroupField';
    case 'hex_color':
      return 'ColorPickerField';
    case 'slider':
      return 'SliderField';
    case 'rating':
      return 'RatingField';
    case 'password':
      return 'PasswordField';
    case 'text_area':
      return 'TextArea';
    case 'phone_number':
      return 'PhoneField';
    case 'gmap_line_1':
    case 'gmap_city':
      return 'AddressLine1';
    case 'payment_method':
      return 'PaymentMethodField';
    default:
      return 'TextField';
  }
};

export const preloadStepFields = (
  step: any,
  visiblePositions: VisiblePositions
) => {
  const fieldComponentKeys = new Set<FieldComponentKey>();
  step.servar_fields.forEach((element: any) => {
    const visibleFlags = visiblePositions[getPositionKey(element)];
    if (visibleFlags?.some(Boolean)) {
      fieldComponentKeys.add(getFieldComponentKey(element.servar?.type));
    }
  });

  return Promise.all(
    Array.from(fieldComponentKeys).map((key) =>
      preloadableFields[key].preload()
    )
  );
};

const Fields = { ...preloadableFields };

const justifyContentTextAlignMap = {
  'flex-start': 'left',
  center: 'center',
  'flex-end': 'right'
};

const defaultBorderFields = [
  'slider',
  'checkbox',
  'multiselect',
  'hex_color',
  'select',
  'signature',
  'file_upload',
  'audio_recording'
];

export const DROPDOWN_Z_INDEX = 10;

function applyLabelFontStyles(styles: any) {
  // skipUnset: unset label_* properties emit no CSS so the label inherits the
  // field's font from its container instead of getting font-style defaults.
  // NOTE: resolveLabelStyle (../labelStyleResolver) mirrors this font-inheritance
  // semantic for UIs that need the concrete displayed value — keep the two in sync.
  styles.applyFontStyles('fieldLabel', false, true, 'label_', true);
}

// Option icons follow an explicit state font color, like button icons do.
const optionIconStateColor = (color: string) =>
  color ? { '[data-feathery-option-icon]': { color: `#${color}` } } : {};

export function applyFieldStyles(field: any, styles: any) {
  const type = field.servar.type;
  styles.addTargets(
    'fc',
    'sub-fc',
    'field',
    'error',
    'active',
    'hover',
    'disabled',
    'tooltipIcon',
    'fieldLabel'
  );

  // For these fields, selector font colors
  // apply to the checkmark / radio fill
  const ignoreSelectorColors = ['select', 'multiselect', 'checkbox'].includes(
    type
  );
  styles.applyFontStyles('fc', false, true);
  styles.applyFontStyles('field', false, ignoreSelectorColors);

  // Label styling is independent of the field. When a label_* property is
  // unset, nothing is applied and the label inherits the field's font from its
  // container ('fc'), preserving prior behavior.
  applyLabelFontStyles(styles);
  // Single checkboxes render their label inline, so CheckboxField owns that gap.
  styles.apply('fieldLabel', 'label_gap', (a: any) =>
    a === undefined || type === 'checkbox' ? {} : { marginBottom: `${a}px` }
  );
  styles.apply('fieldLabel', 'label_text_align', (a: any) =>
    a === undefined ? { textAlign: LABEL_TEXT_ALIGN_DEFAULT } : { textAlign: a }
  );

  // These are fields that don't have content inside, which won't be shifted by
  // a default border
  const hasBorder = defaultBorderFields.includes(type);
  styles.applySelectorStyles('active', {
    prefix: 'selected_',
    important: type !== 'payment_method',
    addBorder: hasBorder,
    background: type === 'button_group'
  });
  styles.applySelectorStyles('hover', {
    prefix: 'hover_',
    important: !['button_group', 'payment_method'].includes(type),
    addBorder: hasBorder,
    background: type === 'button_group'
  });
  styles.apply('disabled', 'disabled_background_color', (a: any) => {
    if (a)
      return {
        [type === 'button_group' ? 'background' : 'backgroundColor']: `#${a}`
      };
    else return { filter: 'brightness(0.9)' };
  });
  styles.applyFontFamily('error');
  styles.apply('error', 'font_size', (a: any) => ({
    fontSize: `${a}px`
  }));
  styles.apply('tooltipIcon', 'font_size', (a: any) => ({
    width: `${a}px`
  }));
  styles.applyColor('tooltipIcon', 'font_color', 'fill');

  // Applied before the switch so the per-type rules there still win. Guarded
  // per side: a side the theme never set is left undeclared, so resetStyles'
  // own padding stands exactly as authored.
  if (INPUT_BOX_FIELDS.includes(type)) {
    styles.apply(
      'field',
      INNER_PADDING_KEYS,
      (a: any, b: any, c: any, d: any) => {
        // Logical inline sides, so the padding follows the form's direction.
        const padding: any = {};
        if (isNum(a)) padding.paddingTop = `${a}px`;
        if (isNum(b)) padding.paddingInlineEnd = `${b}px`;
        if (isNum(c)) padding.paddingBottom = `${c}px`;
        if (isNum(d)) padding.paddingInlineStart = `${d}px`;
        return padding;
      }
    );
    styles.applyInputBoxAlignment(type);
    styles.applyInlineIconPlacement(type);
    if (DROPDOWN_FIELDS.includes(type)) {
      // The right padding places the chevron, and the value stops a chevron
      // strip further in -- one value moves both together, so the padding is
      // exactly the space between the border and the glyph. Unset, the
      // component's own 30px inset stands: the same 10 + 20 this composes to.
      styles.apply('field', INNER_PADDING_RIGHT, (a: any) =>
        a === undefined
          ? {}
          : {
              paddingInlineEnd: `${
                (isNum(a) ? Number(a) : DEFAULT_CHEVRON_GAP) +
                DROPDOWN_CHEVRON_CLEARANCE
              }px`
            }
      );
    }
  }

  switch (type) {
    case 'signature':
      styles.applyHeight('sub-fc');
      styles.applyColor('field', 'background_color', 'backgroundColor');
      styles.applyCorners('field');
      styles.applyBorders({ target: 'field' });
      styles.applyBoxShadow('field');
      break;
    case 'audio_recording':
      styles.applyHeight('sub-fc');
      styles.applyColor('field', 'background_color', 'backgroundColor');
      styles.applyCorners('field');
      styles.applyBorders({ target: 'field' });
      styles.applyBoxShadow('field');
      styles.addTargets('img', 'ac', 'bar');
      styles.applyWidth('img', 'image_');
      styles.applyMargin('img', 'image_');
      styles.applyFlexDirection('ac');
      styles.applyColor('img', 'icon_color', 'color');
      styles.applyColor('bar', 'bar_color', 'backgroundColor');
      break;
    case 'file_upload':
      styles.addTargets('ac', 'add');
      styles.applyHeight('ac');
      if (field.servar.metadata.multiple) {
        styles.apply('field', 'padding_bottom', (a: number) => ({
          marginBottom: `${a}px`
        }));
      }
      styles.applyHeight('field');
      styles.applyBorders({ target: 'field' });
      styles.applyCorners('field');
      styles.applyBoxShadow('field');
      styles.applyColor('field', 'background_color', 'backgroundColor');
      styles.applyColor('ac', 'background_color', 'backgroundColor');
      styles.applyBorders({ target: 'ac' });
      styles.applyCorners('ac');
      styles.applyBoxShadow('ac');
      styles.applyFlexDirection('ac');
      styles.applyWidth('img', 'image_');
      styles.applyMargin('img', 'image_');
      styles.applyColor('img', 'icon_color', 'color');
      styles.apply(
        'ac',
        [
          'uploader_padding_top',
          'uploader_padding_right',
          'uploader_padding_bottom',
          'uploader_padding_left'
        ],
        // @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
        (a, b, c, d) => ({
          padding: `${a}px ${b}px ${c}px ${d}px`
        })
      );
      styles.apply(
        'add',
        [
          'cta_padding_top',
          'cta_padding_right',
          'cta_padding_bottom',
          'cta_padding_left'
        ],
        // @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
        (a, b, c, d) => ({
          padding: `${a}px ${b}px ${c}px ${d}px`
        })
      );
      break;
    case 'rating':
      styles.addTargets('selectedRating', 'hoverRating');
      styles.applyColor('field', 'background_color', 'fill');
      styles.applyColor('selectedRating', 'selected_background_color', 'fill');
      styles.applyColor('hoverRating', 'hover_background_color', 'fill');
      break;
    case 'button_group':
      styles.addTargets('img', 'label', 'bc');
      styles.apply('bc', 'vertical_align', (a: any) => ({
        alignItems: a,
        alignContent: a
      }));
      styles.apply(
        'bc',
        'horizontal_align',
        (a: keyof typeof justifyContentTextAlignMap) => ({
          justifyContent: a,
          textAlign: justifyContentTextAlignMap[a]
        })
      );
      styles.apply(
        'fc',
        'horizontal_align',
        (a: keyof typeof justifyContentTextAlignMap) => ({
          textAlign: justifyContentTextAlignMap[a]
        })
      );
      styles.apply('bc', 'gap', (gap: number) => ({
        gap: gap ? `${gap}px` : undefined
      }));
      styles.applyHeight('bc', '', true);
      styles.apply(
        'field',
        ['button_width', 'button_width_unit', 'content_responsive'],
        (a: any, b: any, c: boolean) => {
          // Fit-width buttons size to their content
          if (isFit(b)) return { width: 'fit-content' };
          const metric = `${a}${b}`;
          if (c) return { minWidth: metric };
          else
            return {
              width: '100%',
              maxWidth: metric
            };
        }
      );
      styles.apply(
        'field',
        ['button_height', 'button_height_unit', 'content_responsive'],
        (a: any, b: any, c: boolean) => {
          // Fit-height buttons size to their content
          if (isFit(b)) return { height: 'fit-content' };
          return { [c ? 'minHeight' : 'height']: `${a}${b}` };
        }
      );
      styles.applyBackgroundColorGradient('field');
      styles.applyBoxShadow('field');
      styles.applyCorners('field');
      styles.applyFlexDirection('field');
      styles.applyContentAlign('field');
      styles.applyTextAlign('label');
      styles.applyMargin('field', 'button_');
      styles.apply(
        'field',
        [
          'uploader_padding_top',
          'uploader_padding_right',
          'uploader_padding_bottom',
          'uploader_padding_left'
        ],
        // @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
        (a, b, c, d) => ({
          padding: `${a}px ${b}px ${c}px ${d}px`
        })
      );
      styles.applyWidth('img', 'image_');
      styles.applyMargin('img', 'image_');
      styles.applyColor('img', 'icon_color', 'color');
      styles.apply('img', 'image_color', getImageColorStyles);
      styles.apply('iconGap', 'flex_direction', nativeIconGapStyles);
      styles.apply('hover', 'hover_image_color', (a: string) => {
        if (!a) return {};
        return {
          'img, [data-feathery-option-icon]': getImageColorStyles(a)
        };
      });
      styles.applyColor('hover', 'hover_font_color', 'color');
      styles.apply('hover', 'hover_font_color', optionIconStateColor);
      styles.applyColor('active', 'selected_font_color', 'color');
      styles.apply('active', 'selected_font_color', optionIconStateColor);

      styles.apply('active', 'selected_image_color', (a: string) => {
        if (!a) return {};
        return {
          'img, [data-feathery-option-icon]': getImageColorStyles(a)
        };
      });
      break;
    case 'dropdown':
    case 'gmap_state':
    case 'gmap_country':
    case 'dropdown_multi':
      if (type === MULTISELECT_FIELD) {
        styles.addTargets('valueContainer');
        styles.applyMultiselectLayout();
        styles.applyMultiselectInnerStyles();
      } else styles.applyHeight('sub-fc');
      styles.applyDropdownChevronPlacement(type);
      styles.applyCorners('sub-fc');
      styles.applyBoxShadow('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      styles.applyCorners('field');
      if (field.properties.placeholder)
        styles.applyPlaceholderStyles(type, field.styles);
      break;
    case 'pin_input':
      styles.applyWidth('sub-fc');
      styles.apply(
        'sub-fc',
        ['width', 'width_unit'],
        (widthValue: any, widthUnit: any) => {
          if (widthUnit === 'px') {
            return { width: '100%', maxWidth: widthValue };
          }
        }
      );
      styles.applyHeight('sub-fc');
      styles.applyBoxShadow('sub-fc');
      styles.applyCorners('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      break;
    case 'multiselect':
      styles.applyCorners('field');
      styles.applyBorders({ target: 'field' });
      styles.applyBoxShadow('field');
      styles.applyColor('field', 'background_color', 'backgroundColor');
      styles.apply('field', 'font_size', (a: any) => ({
        height: `${parseInt(a) + 4}px`
      }));
      break;
    case 'select':
      styles.applyCorners('field');
      styles.applyBorders({ target: 'field' });
      styles.applyBoxShadow('field');
      styles.applyColor('field', 'background_color', 'backgroundColor');
      styles.apply('field', 'font_size', (a: any) => ({
        height: `${parseInt(a) + 4}px`
      }));
      break;
    case 'hex_color':
      styles.applyHeight('field');
      styles.applyCorners('field');
      styles.applyBoxShadow('field');
      styles.applyBorders({ target: 'field' });
      break;
    case 'slider':
      styles.addTargets('handle', 'track');
      styles.apply('handle', ['height', 'height_unit'], (a: any, b: any) => {
        const value = `${a}${b}`;
        return {
          height: value,
          width: value,
          marginTop: `-${Math.max(a - 4, 0) / 2}${b}`
        };
      });
      styles.applyColor('handle', 'background_color', 'backgroundColor');
      styles.applyCorners('handle');
      styles.applyBorders({ target: 'handle' });
      styles.applyBoxShadow('handle');
      styles.applyColor('track', 'background_color', 'backgroundColor');
      styles.apply('field', ['height', 'height_unit'], (a: any, b: any) => {
        const value = `${a / 2 - 6}${b}`;
        return { marginBottom: value };
      });
      break;
    case 'payment_method':
      styles.addTargets('completedFont', 'activeFont', 'hoverFont');
      styles.applyHeight('sub-fc');
      styles.applyCorners('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      styles.applyBoxShadow('sub-fc');

      // iconColor is specific to stripe card element
      styles.applyColor('field', 'placeholder_color', 'iconColor');
      styles.applyColor('hoverFont', 'hover_placeholder_color', 'iconColor');
      styles.applyColor(
        'activeFont',
        'selected_placeholder_color',
        'iconColor'
      );
      styles.applyColor(
        'completedFont',
        'completed_placeholder_color',
        'iconColor'
      );
      styles.applyColor('hoverFont', 'hover_font_color', 'color');
      styles.applyColor('activeFont', 'selected_font_color', 'color');
      styles.applyColor('completedFont', 'completed_font_color', 'color');

      styles.applyPlaceholderStyles(type, field.styles);
      break;
    case 'phone_number':
      styles.addTargets('fieldToggle', 'dropdown');

      styles.applyFontStyles('dropdown', false, true);
      styles.apply('dropdown', 'background_color', (color: any) => {
        if (color.substring(6).toLowerCase() !== 'ff')
          return { backgroundColor: 'white', color: 'black' };
        return { backgroundColor: `#${color}` };
      });

      styles.applyHeight('sub-fc');
      styles.applyBoxShadow('sub-fc');
      styles.applyCorners('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      // Corners must also be applied to input even if not visible since it could cover
      // up the visible container corners
      styles.apply(
        'field',
        ['corner_top_right_radius', 'corner_bottom_right_radius'],
        // @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
        (a, b) => ({ borderRadius: `0 ${a}px ${b}px 0` })
      );
      // The left padding places the flag (applyPhoneFlagPlacement); the number
      // keeps the fixed gap it has always had from it, so the theme's value
      // never reaches the input. Pinned in rem, as resetStyles authors it, and
      // only once the theme sets the key at all.
      styles.apply('field', INNER_PADDING_LEFT, (l: any) =>
        l === undefined ? {} : { paddingInlineStart: RESET_INLINE_PADDING_CSS }
      );
      styles.applyPhoneFlagPlacement();
      styles.applyPlaceholderStyles(type, field.styles);

      styles.apply('fieldToggle', 'font_size', (a: any) => ({
        fontSize: `${1.5 * a}px`,
        width: `${3 * a}px`
      }));
      styles.apply(
        'fieldToggle',
        ['corner_top_left_radius', 'corner_bottom_left_radius'],
        (a: any, b: any) => ({
          borderStartStartRadius: `${a}px`,
          borderEndStartRadius: `${b}px`
        })
      );
      styles.apply('fieldToggle', 'border_bottom_color', (a: any) => ({
        borderInlineEnd: `1px solid #${a}`
      }));
      break;
    case 'qr_scanner':
      styles.applyBoxShadow('sub-fc');
      styles.applyBorders({ target: 'sub-fc' });
      styles.applyCorners('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      break;
    default:
      styles.addTargets('dropdown');
      styles.applyFontStyles('dropdown');
      styles.apply('dropdown', 'background_color', (color: any) => {
        if (color.substring(6).toLowerCase() !== 'ff')
          return { backgroundColor: 'white', color: 'black' };
        return { backgroundColor: `#${color}` };
      });

      styles.applyHeight('sub-fc');
      styles.applyCorners('sub-fc');
      styles.applyColor('sub-fc', 'background_color', 'backgroundColor');
      styles.applyBoxShadow('field');
      styles.applyCorners('field');
      if (field.properties.placeholder)
        styles.applyPlaceholderStyles(type, field.styles);
      break;
  }
  // After the switch: applyHeight writes the box's height there, and a
  // percentage height brings a floor of its own to merge with.
  if (INPUT_BOX_FIELDS.includes(type) || type === MULTISELECT_FIELD)
    styles.applyInputBoxMinHeight(type);
  return styles;
}

Object.entries(Fields).map(([key, Field]: any) => {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  Fields[key] = memo(
    ({ element, responsiveStyles, labelCallbacks, ...props }: any) => {
      const servar = element.servar;
      const styles = useMemo(
        () => applyFieldStyles(element, responsiveStyles),
        [element]
      );
      const markRequired =
        servar?.required && element.styles?.mark_required_asterisk;
      // Label editing is only enabled in the form builder canvas
      const labelEditable =
        props.editMode === 'editable' && !!labelCallbacks?.setLabel;
      const fieldLabel = servar?.name ? (
        <label
          // Doesn't work for repeats currently since repeating field IDs aren't unique
          htmlFor={servar.repeated || labelEditable ? undefined : servar.key}
          style={{
            marginBottom: `${getLabelGapDefault(servar.type)}px`,
            display: 'inline-block',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            ...styles.getTarget('fieldLabel')
          }}
        >
          {labelEditable ? (
            <EditableFieldLabel
              elementId={element.id}
              label={servar.name}
              focused={props.focused}
              setLabel={labelCallbacks.setLabel}
            />
          ) : (
            servar.name
          )}
          {markRequired && (
            <span
              aria-hidden='true'
              style={{ color: '#e11900', marginLeft: '2px' }}
            >
              *
            </span>
          )}
        </label>
      ) : null;
      return (
        <Field
          element={element}
          fieldLabel={fieldLabel}
          responsiveStyles={styles}
          {...props}
        />
      );
    }
  );
});

export default Fields;
